/**
 * Centralized Color Theme Configuration
 *
 * 🎨 TO CHANGE THE THEME: Only edit the BASE section below
 * All other colors are automatically derived from base colors
 *
 * 🌈 NODE CATEGORY COLORS: Change categoryColorMode below
 */

// ========================================
// 🎨 COLOR GENERATION UTILITIES
// ========================================

// Parse rgb/rgba string to {r, g, b}
const parseRgb = (color) => {
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
  }
  return { r: 0, g: 0, b: 0 };
};

// Convert RGB to HSL
const rgbToHsl = (color) => {
  const { r, g, b } = parseRgb(color);
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
      case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
      case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

// Convert HSL to RGB string
const hslToRgb = (h, s, l) => {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r, g, b;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  return `rgb(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)})`;
};

// Add alpha to RGB color
const withAlpha = (color, alpha) => {
  const { r, g, b } = parseRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ========================================
// 🌈 CATEGORY COLOR GENERATION METHODS
// ========================================

/**
 * Method 1: HUE_ROTATION
 * Rotates hue around the color wheel (36° each for 10 colors)
 * Creates distinct but harmonious colors
 */
const generateHueRotation = (baseColor, count = 10) => {
  const hsl = rgbToHsl(baseColor);
  const colors = [];

  for (let i = 0; i < count; i++) {
    const hue = (hsl.h + (i * 360 / count)) % 360;
    colors.push(hslToRgb(hue, Math.min(hsl.s + 20, 70), Math.max(hsl.l + 15, 45)));
  }

  return colors;
};

/**
 * Method 2: SATURATION_LIGHTNESS
 * Keeps same hue, varies saturation and lightness
 * All colors are shades/tints of the theme color
 */
const generateSaturationLightness = (baseColor, count = 10) => {
  const hsl = rgbToHsl(baseColor);
  const colors = [];

  const variations = [
    { s: 0, l: 25 },
    { s: -15, l: 15 },
    { s: 10, l: 35 },
    { s: -25, l: 5 },
    { s: 15, l: 20 },
    { s: -10, l: 30 },
    { s: 5, l: 10 },
    { s: -20, l: 40 },
    { s: 20, l: 0 },
    { s: -5, l: 45 },
  ];

  for (let i = 0; i < count; i++) {
    const v = variations[i % variations.length];
    colors.push(hslToRgb(
      hsl.h,
      Math.max(20, Math.min(90, hsl.s + v.s)),
      Math.max(25, Math.min(75, hsl.l + v.l))
    ));
  }

  return colors;
};

/**
 * Method 3: TRIADIC_VARIATIONS
 * Uses color theory (primary, complementary, triadic, split-complementary)
 * More structured, theory-based palette
 */
const generateTriadicVariations = (baseColor, count = 10) => {
  const hsl = rgbToHsl(baseColor);
  const colors = [];

  const colorScheme = [
    { angle: 0, sAdj: 0, lAdj: 20 },
    { angle: 180, sAdj: -10, lAdj: 15 },
    { angle: 120, sAdj: 5, lAdj: 25 },
    { angle: 240, sAdj: 5, lAdj: 25 },
    { angle: 30, sAdj: 10, lAdj: 20 },
    { angle: 330, sAdj: 10, lAdj: 20 },
    { angle: 150, sAdj: -5, lAdj: 30 },
    { angle: 210, sAdj: -5, lAdj: 30 },
    { angle: 60, sAdj: 15, lAdj: 15 },
    { angle: 270, sAdj: 0, lAdj: 35 },
  ];

  for (let i = 0; i < count; i++) {
    const scheme = colorScheme[i % colorScheme.length];
    colors.push(hslToRgb(
      (hsl.h + scheme.angle) % 360,
      Math.max(30, Math.min(80, hsl.s + scheme.sAdj)),
      Math.max(35, Math.min(65, hsl.l + scheme.lAdj))
    ));
  }

  return colors;
};

// ========================================
// 🎨 BASE COLORS - EDIT THESE TO CHANGE THEME
// ========================================

/**
 * 🌈 CATEGORY COLOR MODE
 * Choose how node category colors are generated from themePrimary:
 * - 'HUE_ROTATION': Distinct colors rotating around color wheel (most variety)
 * - 'SATURATION_LIGHTNESS': Monochromatic shades of theme color (unified look)
 * - 'TRIADIC_VARIATIONS': Color theory based palette (balanced harmony)
 */
const categoryColorMode = 'SATURATION_LIGHTNESS';

const themePrimary = 'rgb(15, 35, 75)';        // 🎨 CHANGE THIS for different theme
const themeSecondary = 'rgba(255, 255, 255)';        // 🎨 CHANGE THIS for different theme
const themeTertiary = 'rgb(240, 236, 229)';    // 🎨 CHANGE THIS — off-white used for parchment/modal backgrounds
// const themePrimary = 'rgb(30, 27, 61)';        // 🎨 CHANGE THIS for different theme (old purple)

const base = {
  // Main theme color
  themePrimary: themePrimary,
  themePrimaryTransparent: withAlpha(themePrimary, 0.9),
  themePrimaryVariant: themePrimary,  // Kept identical to themePrimary so all derived UI stays uniform
  themeSecondary: themeSecondary,  // Auto-generated lighter variant
  themeSecondaryTransparent: withAlpha(themeSecondary, 0.2),  // Auto-generated lighter variant
  themeTertiary: themeTertiary,  // Off-white background (parchment box, modal backgrounds)

  // Accent colors (currently white/orange)
  accentPrimary: 'rgb(255, 255, 255)',         // 🎨 CHANGE THIS for button text, highlights
  accentSecondary: 'rgba(255, 255, 255, 0.6)', // 🎨 CHANGE THIS for dimmer accent variant
  accentOrange: 'rgb(255, 181, 71)',           // 🎨 CHANGE THIS for borders, bullets

  // Admin colors
  adminActive: 'rgb(231, 76, 60)',             // 🎨 Admin ON color (red)
  adminInactive: 'rgb(127, 140, 141)',         // 🎨 Admin OFF color (gray)

  // Status colors
  success: 'rgb(76, 175, 80)',                 // 🎨 Success color (green)
  error: 'rgb(244, 67, 54)',                   // 🎨 Error color (red)

  // Neutral colors (rarely need changing)
  white: 'rgb(255, 255, 255)',
  black: 'rgb(0, 0, 0)',
  darkText: 'rgb(51, 51, 51)',
  lightGray: 'rgb(245, 245, 245)',
  borderGray: 'rgb(221, 221, 221)',
};

// ========================================
// AUTO-GENERATED COLORS - Don't edit below
// These are computed from base colors
// ========================================

// Purple variant used in various components (lighter than theme primary)
const purpleVariant = 'rgb(40, 70, 130)';
// Focus blue color
const focusBlue = 'rgb(74, 144, 226)';
// Primary accent for highlights
const primaryHighlight = 'rgb(255, 255, 255)';

const colors = {
  base,

  primary: {
    dark: base.themePrimary,
    darkVariant: base.themePrimaryVariant,
    darkRgba: withAlpha(base.themePrimaryVariant, 0.8),
    darkRgbaLight: withAlpha(base.themePrimaryVariant, 0.1),
    // Additional alpha variants
    dark95: withAlpha(base.themePrimary, 0.95),
    dark90: withAlpha(base.themePrimary, 0.9),
    dark40: withAlpha(base.themePrimary, 0.4),
    dark30: withAlpha(base.themePrimary, 0.3),
    dark20: withAlpha(base.themePrimary, 0.2),
    dark15: withAlpha(base.themePrimary, 0.15),
  },

  // Purple variant (lighter purple used in navigation, labels)
  purple: {
    base: purpleVariant,
    alpha80: withAlpha(purpleVariant, 0.8),
    alpha70: withAlpha(purpleVariant, 0.7),
    alpha60: withAlpha(purpleVariant, 0.6),
    alpha50: withAlpha(purpleVariant, 0.5),
    alpha35: withAlpha(purpleVariant, 0.35),
    alpha30: withAlpha(purpleVariant, 0.3),
  },

  accent: {
    primary: base.accentPrimary,
    secondary: base.accentSecondary,
    primaryHighlight: primaryHighlight,
    primaryHighlight90: withAlpha(primaryHighlight, 0.9),
    orange: withAlpha(base.accentOrange, 0.8),
    orangeLight: withAlpha(base.accentOrange, 0.15),
    // Additional orange alpha variants
    orange95: withAlpha(base.accentOrange, 0.95),
    orange90: withAlpha(base.accentOrange, 0.9),
    orange70: withAlpha(base.accentOrange, 0.7),
    orange60: withAlpha(base.accentOrange, 0.6),
    orange50: withAlpha(base.accentOrange, 0.5),
    orange40: withAlpha(base.accentOrange, 0.4),
    orange30: withAlpha(base.accentOrange, 0.3),
    orange20: withAlpha(base.accentOrange, 0.2),
  },

  // Focus/highlight blue
  focus: {
    base: focusBlue,
    alpha40: withAlpha(focusBlue, 0.4),
    alpha10: withAlpha(focusBlue, 0.1),
  },

  text: {
    light: 'rgba(255, 255, 255, 0.95)',
    lightSecondary: 'rgba(255, 255, 255, 0.6)',
    dark: base.darkText,
    purple: withAlpha(base.themePrimaryVariant, 0.9),
    white: base.white,
    // Additional variants
    white90: 'rgba(255, 255, 255, 0.9)',
    white80: 'rgba(255, 255, 255, 0.8)',
    white70: 'rgba(255, 255, 255, 0.7)',
  },

  background: {
    white: base.white,
    lightGray: base.lightGray,
    overlay: 'rgba(0, 0, 0, 0.7)',
    overlayLight: 'rgba(0, 0, 0, 0.5)',
    transparent: 'rgba(255, 255, 255, 0.05)',
    // Additional white alpha variants
    white95: 'rgba(255, 255, 255, 0.95)',
    white90: 'rgba(255, 255, 255, 0.9)',
    white80: 'rgba(255, 255, 255, 0.8)',
    white30: 'rgba(255, 255, 255, 0.3)',
    white20: 'rgba(255, 255, 255, 0.2)',
    white15: 'rgba(255, 255, 255, 0.15)',
    white10: 'rgba(255, 255, 255, 0.1)',
    // Black alpha variants
    black90: 'rgba(0, 0, 0, 0.9)',
    black50: 'rgba(0, 0, 0, 0.5)',
    black40: 'rgba(0, 0, 0, 0.4)',
    black35: 'rgba(0, 0, 0, 0.35)',
    black30: 'rgba(0, 0, 0, 0.3)',
    black20: 'rgba(0, 0, 0, 0.2)',
    black10: 'rgba(0, 0, 0, 0.1)',
    black05: 'rgba(0, 0, 0, 0.05)',
  },

  border: {
    light: 'rgba(255, 255, 255, 0.2)',
    lightHover: 'rgba(255, 255, 255, 0.4)',
    lightStrong: 'rgba(255, 255, 255, 0.5)',
    lightSubtle: 'rgba(255, 255, 255, 0.1)',
    lightMedium: 'rgba(255, 255, 255, 0.3)',
    lightWeak: 'rgba(255, 255, 255, 0.125)',
    gray: base.borderGray,
    purple: withAlpha(base.themePrimaryVariant, 0.3),
    orange: withAlpha(base.accentOrange, 0.8),
    orange50: withAlpha(base.accentOrange, 0.5),
    orange30: withAlpha(base.accentOrange, 0.3),
  },

  button: {
    // Primary buttons
    primaryBg: withAlpha(base.themePrimaryVariant, 0.9),
    primaryBgHover: base.themePrimaryVariant,
    primaryText: base.accentPrimary,
    primaryBorder: 'rgba(160, 163, 179, 0.38)',

    // About/Story buttons
    aboutBg: base.themePrimary,
    aboutBgHover: 'rgba(255, 255, 255, 0.2)',
    aboutText: 'rgba(255, 255, 255, 0.95)',
    aboutBorder: 'rgba(255, 255, 255, 0.2)',

    // Admin toggle button
    adminActiveBg: withAlpha(base.adminActive, 0.9),
    adminActiveBgHover: 'rgb(192, 57, 43)',
    adminActiveBorder: withAlpha(base.adminActive, 0.5),
    adminInactiveBg: withAlpha(base.adminInactive, 0.9),
    adminInactiveBgHover: 'rgb(149, 165, 166)',
    adminInactiveBorder: 'rgba(255, 255, 255, 0.2)',
    adminText: base.white,
  },

  modal: {
    background: base.themePrimary,
    titleColor: 'rgba(255, 255, 255, 0.95)',
    contentBg: base.white,
    shadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },

  status: {
    successBg: withAlpha(base.success, 0.15),
    successBg20: withAlpha(base.success, 0.2),
    successBorder: withAlpha(base.success, 0.5),
    successText: 'rgb(46, 125, 50)',

    errorBg: withAlpha(base.error, 0.15),
    errorBg20: withAlpha(base.error, 0.2),
    errorBorder: withAlpha(base.error, 0.5),
    errorText: 'rgb(198, 40, 40)',

    warningBg: withAlpha(base.accentOrange, 0.15),
    warningBorder: withAlpha(base.accentOrange, 0.8),
    warningText: base.accentOrange,
  },

  ui: {
    // Header important banner
    importantBg: withAlpha(base.accentOrange, 0.15),
    importantBorder: withAlpha(base.accentOrange, 0.8),
    importantText: base.accentOrange,

    // Premium badge
    premiumBadge: 'rgb(224, 208, 114)',
    premiumBadge10: 'rgba(255, 215, 0, 0.1)',

    // minimap
    minimap: withAlpha(base.themeSecondary, 0.1),

    // Pagination dots
    dotActive: base.accentPrimary,
    dotInactive: 'rgba(255, 255, 255, 0.3)',

    // Scrollbar — white to match the site identity (was gold/orange)
    scrollbarThumb: 'rgba(255, 255, 255, 0.45)',
    scrollbarThumbHover: 'rgba(255, 255, 255, 0.65)',
    scrollbarTrack: 'rgba(255, 255, 255, 0.1)',
    scrollbarTrackDark: 'rgba(0, 0, 0, 0.2)',

    // Tap highlight for mobile
    tapHighlight: withAlpha(base.themeSecondary, 0.2),
  },

  effects: {
    shadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    shadowHover: '0 4px 12px rgba(0, 0, 0, 0.15)',
    shadowMedium: '0 4px 15px rgba(0, 0, 0, 0.2)',
    shadowStrong: '0 6px 20px rgba(0, 0, 0, 0.3)',
    shadowLarge: '0 20px 40px rgba(0, 0, 0, 0.5)',
    shadowXLarge: '0 20px 60px rgba(0, 0, 0, 0.5)',
    // Text shadows
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
    textShadowMedium: '2px 2px 4px rgba(0, 0, 0, 0.3)',
    textShadowGlow: '0 2px 10px rgba(255, 181, 71, 0.3)',
    // Drop shadows
    dropShadow: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
    dropShadowGlow: 'drop-shadow(0 0 8px rgba(255, 181, 71, 0.8))',
  },

  // Gradient helpers
  gradients: {
    whiteShine: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent)',
    whiteShineStrong: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
    whiteShineHover: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))',
    orangeButton: 'linear-gradient(135deg, rgba(255, 181, 71, 0.9) 0%, rgba(255, 140, 0, 0.9) 100%)',
    orangeButtonHorizontal: 'linear-gradient(90deg, rgba(255, 181, 71, 0.9) 0%, rgba(255, 140, 0, 0.9) 100%)',
    modalBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9))',
  },
};

// ========================================
// 🌈 NODE CATEGORY COLORS (auto-generated)
// ========================================

const generateCategoryColors = () => {
  switch (categoryColorMode) {
    case 'HUE_ROTATION':
      return generateHueRotation(base.themePrimary, 10);
    case 'SATURATION_LIGHTNESS':
      return generateSaturationLightness(base.themePrimary, 10);
    case 'TRIADIC_VARIATIONS':
      return generateTriadicVariations(base.themePrimary, 10);
    default:
      return generateHueRotation(base.themePrimary, 10);
  }
};

/**
 * Node category colors - indexed 0-9 for categories 1-10
 * These are derived from themePrimary based on categoryColorMode
 */
colors.nodeCategories = generateCategoryColors();

// Export the mode for reference
colors.categoryColorMode = categoryColorMode;

// Export utilities for external use
colors.utils = {
  rgbToHsl,
  hslToRgb,
  withAlpha,
  parseRgb,
  generateHueRotation,
  generateSaturationLightness,
  generateTriadicVariations,
};

export default colors;
